import { useEffect, useState } from 'react';
// @mui
import Container from '@mui/material/Container';
import { CircularProgress } from '@mui/material';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import LoadingButton from '@mui/lab/LoadingButton';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// components
import useFetcher, { endpoints } from 'src/utils/axios';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import Grid from '@mui/material/Grid';
import { useBoolean } from 'src/hooks/use-boolean';

export default function UserConsent() {
  const settings = useSettingsContext();

  const { fetchData, updateData } = useFetcher();
  const isLoading = useBoolean(true);

  // Track switches
  const [termsChecked, setTermsChecked] = useState(true);
  const [newsletterChecked, setNewsletterChecked] = useState(false);

  // Dual timestamps
  const [termsTimestamp, setTermsTimestamp] = useState('');
  const [newsletterTimestamp, setNewsletterTimestamp] = useState('');

  useEffect(() => {
    fetchData(endpoints.consent.getUserConsent)
      .then((res) => {
        if (res) {
          setTermsChecked(res.check_terms);
          setNewsletterChecked(res.check_newsletters);
          setTermsTimestamp(res.terms_last_modified_date);
          setNewsletterTimestamp(res.newsletters_last_modified_date);
        } else {
          // No consent yet: initialize defaults
          setTermsChecked(true);
          setNewsletterChecked(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching consent:', error);
      })
      .finally(() => {
        isLoading.onFalse();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubmitting = () => {
    const data = {
      terms: termsChecked,
      newsletters: newsletterChecked,
    };

    updateData(endpoints.consent.updateUserConsent, data);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading="Consent"
        links={[{ name: 'User Settings' }]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Grid container spacing={1}>
        <Grid xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            {isLoading.value ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                {/* Terms of Service */}
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled
                        checked={termsChecked}
                        onChange={(e) => setTermsChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{' '}
                        <Link
                          component={RouterLink}
                          href={paths.legal.termsOfService}
                          sx={{ color: 'primary.main' }}
                        >
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link
                          component={RouterLink}
                          href={paths.legal.privacyPolicy}
                          sx={{ color: 'primary.main' }}
                        >
                          Privacy Policy
                        </Link>
                        .
                      </Typography>
                    }
                  />
                  <Typography variant="caption" sx={{ pl: 4, mt: 0.5, display: 'block' }}>
                    {termsTimestamp
                      ? `Date Modified: ${termsTimestamp}`
                      : 'Set the consent to see the last modified date for terms'}
                  </Typography>
                </Box>

                {/* Improvements toggle + timestamp */}
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newsletterChecked}
                        onChange={(e) => setNewsletterChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Keep up with our constant improvements through our newsletter."
                  />
                  <Typography variant="caption" sx={{ pl: 4, mt: 0.5, display: 'block' }}>
                    {newsletterTimestamp
                      ? `Date Modified: ${newsletterTimestamp}`
                      : 'Set the consent to see the last modified date for consent Marketing.'}
                  </Typography>
                </Box>

                {/* Save button */}
                <Stack alignItems="flex-end">
                  <LoadingButton onClick={isSubmitting} variant="contained">
                    Save Changes
                  </LoadingButton>
                </Stack>
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
